// Field mesh — read from OUR server's /api/field, not a public weather API. The
// server holds one pre-fetched GFS-Wave grid (wind + wave) resident
// (db/field/fetch_field.py → Postgres → server/field.go) and hands back only the
// subset a client asks for. One request, cached server-side per model cycle: no
// per-user rate-limit fragility, and commercially-clean public-domain data.
//
// The subset is requested as a BOUNDING BOX rather than as a steaming reach around
// the site: a box does not move when the site is dragged inside it, and it can be
// computed without knowing a vessel's cruise speed, which a reach disc cannot.
//
// The map asks for the WINDOW IT IS SHOWING and gets the finest grid that fits the
// cell budget. What the budget buys is never bytes — the response always carries
// every time step of both variables, so its size is (cells x steps x 2) and the
// cell count is the only term a client controls. It buys WHERE those cells are
// spent. See fieldDefaultMaxCells in server/field.go.

import { reportSystem } from "../ops/health.js";

export type FieldKind = "wind" | "wave";

export interface FieldMesh {
  latMin: number;
  latMax: number;
  lonMin: number;
  lonMax: number;
  nx: number; // columns (longitude)
  ny: number; // rows (latitude, latMin at row 0)
  /** Valid times as sent by the server, e.g. "2026-07-20T00:00:00Z".
   *  NOT evenly spaced: GFS-Wave publishes hourly to f120, then 3-hourly to f384.
   *  Never derive an index from wall-clock arithmetic — use indexAtInstant(). */
  timesUtc: string[];
  /** timesUtc parsed to epoch ms, once, at fetch. Same length and order. */
  times: number[];
  /** One Float32Array per hour, length nx*ny, row-major (index = iy*nx + ix). NaN = land/missing. */
  wind: Float32Array[]; // kn
  wave: Float32Array[]; // m — NaN on land, so also the sea mask
  /** Direction planes, same shape and NaN convention as wind/wave. Compass degrees
   *  0–360, "FROM" convention (Open-Meteo wind_direction_10m / wave_direction) — a
   *  drawer that wants travel direction must add 180, like the chart ribbons do.
   *  OPTIONAL: a server that predates them sends no windDir/waveDir keys and the
   *  mesh simply carries none. Everything that draws arrows feature-detects on
   *  these being present rather than assuming the server is current. */
  windDir?: Float32Array[]; // deg
  waveDir?: Float32Array[]; // deg
  /** Model cycle ("2026072012") and source, as the server states them. The parser
   *  used to DISCARD both, which left the map the only surface on screen that
   *  never said what model it was or how fresh — every chart line names its
   *  model, and the field's own line said only "Map field". */
  cycle: string;
  source: string;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Parse a server valid-time to epoch ms. The server sends a trailing "Z", but be
 *  tolerant of a bare stamp too and read that as UTC — appending an unconditional
 *  "Z" to a string that already carries one yields NaN, which is exactly the bug
 *  that silently pinned the scrubber to the start of the cycle. */
export function parseValidTime(s: string): number {
  const hasZone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(s);
  const t = Date.parse(hasZone ? s : s + "Z");
  return Number.isFinite(t) ? t : NaN;
}

/** Index of the step whose valid time is nearest `instant` (epoch ms).
 *
 *  This must be a search, not `(instant - t0) / 3600000`. Steps are hourly only to
 *  f120 and 3-hourly beyond, so the arithmetic form drifts past five days — at the
 *  end of a 7-day run it is wrong by ~48 h, pointing the map at a completely
 *  different forecast hour than the one the timeline says it is showing. */
export function indexAtInstant(mesh: FieldMesh, instant: number): number {
  const t = mesh.times;
  const n = t.length;
  if (n === 0) return 0;
  if (n === 1 || !Number.isFinite(instant)) return 0;
  if (instant <= t[0]) return 0;
  if (instant >= t[n - 1]) return n - 1;
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (t[mid] <= instant) lo = mid;
    else hi = mid;
  }
  return instant - t[lo] <= t[hi] - instant ? lo : hi;
}

/**
 * The outcome of asking for a mesh, with the two failures kept apart.
 *
 * They used to collapse into one `null`, and that made a dropped request
 * indistinguishable from "there is no field here" — so a single network blip cleared
 * the wind/wave layer off the map and left a bare basemap that looked exactly like
 * the layer having been switched off. On a go/no-go console the hazard layer silently
 * vanishing is the failure mode that matters most, and the exposure grew by orders of
 * magnitude when fetching went from once per basin to once per map gesture.
 *
 *   "none"  — the server answered 204: authoritative, there is genuinely no grid here.
 *             Clear the field; it would be a lie to keep painting.
 *   "error" — we failed to ASK (network, 5xx, unparseable body). We know nothing new,
 *             so the honest move is to keep what is on screen and try again.
 */
export type FieldFetch =
  | { ok: true; mesh: FieldMesh }
  | { ok: false; reason: "none" | "error" };

export const NO_COVERAGE: FieldFetch = { ok: false, reason: "none" };
export const FETCH_FAILED: FieldFetch = { ok: false, reason: "error" };

/** The rectangle to fetch — in practice a theater's bounds. */
export interface FieldBounds {
  latMin: number;
  latMax: number;
  lonMin: number;
  lonMax: number;
}

/** The spacing of the pre-fetched GFS-Wave grid, in degrees. Mirrored from the
 *  server's grid rather than read off a response so a client can work out what a
 *  request WOULD come back as before it makes it. */
export const FIELD_CELL_DEG = 0.25;

/** The server's default cell budget for the bbox form — fieldDefaultMaxCells in
 *  server/field.go. Mirrored for the same reason as FIELD_CELL_DEG; if the two ever
 *  drift the only cost is a slightly wrong prediction of the stride, which makes a
 *  refetch decision marginally early or late, never wrong. */
export const FIELD_DEFAULT_MAX_CELLS = 8000;

/** The stride the server would coarsen an nx-by-ny cell window to, to fit `budget`.
 *  The exact loop from strideFor in server/field.go — one stride for both axes, so a
 *  mesh has a single cell size and a caller can compare two of them as one number. */
export function strideFor(nx: number, ny: number, budget: number): number {
  let stride = 1;
  if (budget <= 0) return stride;
  let outNx = nx;
  let outNy = ny;
  while (outNx * outNy > budget) {
    stride++;
    outNx = Math.ceil(nx / stride);
    outNy = Math.ceil(ny / stride);
  }
  return stride;
}

/** The cell size of a mesh in degrees — its resolution, as one comparable number.
 *  Read off the mesh's own bounds and shape rather than assumed, because the whole
 *  point of the budget is that the server may have coarsened by any stride. */
export function meshCellDeg(m: FieldMesh): number {
  return m.ny > 1 ? (m.latMax - m.latMin) / (m.ny - 1) : FIELD_CELL_DEG;
}

/**
 * A point-forecast time label ("2026-07-11T14:00", local wall clock, no zone) as a
 * true epoch. Mirrors computeNowIndex() in openmeteo.ts — the one conversion this
 * codebase already trusts — rather than inventing a fourth reading of these labels.
 * A label that DOES carry a zone is taken at its word.
 */
function siteEpoch(label: string, utcOffsetSeconds: number): number {
  if (/[Zz]$|[+-]\d{2}:?\d{2}$/.test(label)) return Date.parse(label);
  const withSecs = /T\d{2}:\d{2}$/.test(label) ? `${label}:00` : label;
  const asUtc = Date.parse(`${withSecs}Z`);
  return Number.isFinite(asUtc) ? asUtc - utcOffsetSeconds * 1000 : NaN;
}

/** What a mesh sample actually represents, so a caller can say so on screen. */
export interface MeshSample {
  /** Values aligned 1:1 with the `targetTimes` passed in. Null everywhere the field
   *  has no step — which is most hours past f120, where it turns 3-hourly. */
  data: (number | null)[];
  /** Cell size in degrees, and the distance in nm from the requested point to the
   *  centre of the cell that answered. Both belong on the label: the difference
   *  between this trace and a point forecast is partly model and partly the fact
   *  that this is a cell average up to 30 nm away. */
  cellDeg: number;
  offsetNm: number;
}

/**
 * The field's own value at a point, on a target time axis — the map's model, drawn
 * as one more line on a chart of models.
 *
 * The field is a single deterministic run (NOAA GFS-Wave) while the charts carry a
 * multi-model spread and a GEFS ensemble from Open-Meteo. Those disagree, and hiding
 * that makes the map an unaudited second opinion. Drawn together, the gap between
 * this line and the ensemble IS the honest, flow-dependent statement of how much to
 * trust today's colours — better than any climatological error bar, because it is
 * measured here, now, at this lead time.
 *
 * Two things this deliberately does NOT do. It never interpolates in time: a value
 * lands only on a target hour that a real model step falls on, so past f120 the line
 * goes sparse and SHOWS you the field is 3-hourly there. And it never interpolates in
 * space: it reads the one cell containing the point and reports how far off-centre
 * that was, because a smoothed value would invent precision the grid does not have.
 */
export function meshSeriesAt(
  mesh: FieldMesh,
  lat: number,
  lon: number,
  kind: FieldKind,
  targetTimes: string[],
  /**
   * The site's offset from UTC, as Open-Meteo reported it. NOT optional, and not
   * defaulted: `targetTimes` are LOCAL WALL-CLOCK labels with no zone ("2026-07-11T14:00")
   * because the point forecast is fetched with `timezone=auto`, while `mesh.times` are
   * true UTC instants off our own GRIB pipeline. Reading the labels as UTC — which is
   * what a bare Date.parse does — silently displaces this whole trace by the offset:
   * five hours in the Gulf, three in Brazil, zero only in West Africa. It cannot be
   * caught by the tolerance below, because both sides would then be wrong in the same
   * frame and the gap would compute as zero. Same conversion as computeNowIndex().
   */
  utcOffsetSeconds: number,
  toleranceMs = 30 * 60 * 1000,
): MeshSample | null {
  if (mesh.nx < 1 || mesh.ny < 1) return null;
  const dLat = mesh.ny > 1 ? (mesh.latMax - mesh.latMin) / (mesh.ny - 1) : 0;
  const dLon = mesh.nx > 1 ? (mesh.lonMax - mesh.lonMin) / (mesh.nx - 1) : 0;
  const iy = dLat > 0 ? Math.round((lat - mesh.latMin) / dLat) : 0;
  const ix = dLon > 0 ? Math.round((lon - mesh.lonMin) / dLon) : 0;
  if (iy < 0 || iy >= mesh.ny || ix < 0 || ix >= mesh.nx) return null;

  const cellLat = mesh.latMin + iy * dLat;
  const cellLon = mesh.lonMin + ix * dLon;
  const offsetNm =
    Math.hypot(cellLat - lat, (cellLon - lon) * Math.cos((lat * Math.PI) / 180)) * 60;

  // Target hour -> slot, so steps are PLACED on the axis rather than resampled onto
  // it. Keyed on the hour the step falls in; the tolerance then rejects a step that
  // merely shares an hour without being near its start.
  const slot = new Map<number, number>();
  const epochs: number[] = new Array(targetTimes.length).fill(NaN);
  for (let i = 0; i < targetTimes.length; i++) {
    const t = siteEpoch(targetTimes[i], utcOffsetSeconds);
    epochs[i] = t;
    // Later index wins a collision, which is what a DST fall-back produces: two
    // local labels naming the same hour. The earlier of the pair is left null
    // rather than being given the wrong step's value.
    if (!Number.isNaN(t)) slot.set(Math.round(t / 3_600_000), i);
  }

  const src = kind === "wave" ? mesh.wave : mesh.wind;
  const data: (number | null)[] = new Array(targetTimes.length).fill(null);
  const idx = iy * mesh.nx + ix;
  for (let s = 0; s < mesh.times.length && s < src.length; s++) {
    const t = mesh.times[s];
    if (!Number.isFinite(t)) continue;
    const i = slot.get(Math.round(t / 3_600_000));
    if (i === undefined) continue;
    const target = epochs[i];
    if (Number.isNaN(target) || Math.abs(target - t) > toleranceMs) continue;
    const v = src[s]?.[idx];
    if (v === undefined || Number.isNaN(v)) continue;
    data[i] = v;
  }
  return data.some((v) => v !== null)
    ? { data, cellDeg: dLat > 0 ? dLat : FIELD_CELL_DEG, offsetNm }
    : null;
}

/** Does `outer` contain the whole of `inner`? A FieldMesh is an acceptable `outer`,
 *  and its own bounds are the right ones to test: they are what the renderer draws
 *  against, and a mesh that merely overlaps the window would be stretched over water
 *  it holds no values for. */
export function boundsCover(outer: FieldBounds, inner: FieldBounds): boolean {
  return (
    outer.latMin <= inner.latMin &&
    outer.latMax >= inner.latMax &&
    outer.lonMin <= inner.lonMin &&
    outer.lonMax >= inner.lonMax
  );
}

/** The stride a bbox request would come back at: the box in whole grid cells (snapped
 *  outward, hence the +1), reduced until it fits the budget. */
export function strideForBounds(b: FieldBounds, budget: number): number {
  // +2, not +1. The server does not count the cells inside the box — it snaps
  // OUTWARD to whole grid cells first (floor on the low edge, ceil on the high one)
  // and counts those, so for any box whose edges are not grid-aligned it sees one
  // more cell per axis than a naive span/cell + 1. And no box is grid-aligned: these
  // are padded MapLibre viewports sent at four decimal places against a grid origin
  // of lat -40 / lon -100.
  //
  // Under-counting here is not a rounding nit, it is a permanent refetch state. The
  // client would predict stride 1, the server would answer stride 2, and the
  // coarseness test would demand a 0.25-degree mesh the server will never return for
  // that box — refetching a couple of megabytes on every moveend, forever, at roughly
  // 1.5% of realistic viewports. Over-counting by one is harmless by comparison: the
  // worst case is holding a mesh one stride coarser than strictly affordable.
  // ceil(S) + 2 is the exact upper bound on what the server counts. With
  // lo = (boxMin - gridOrigin)/cell and S = span/cell, it takes
  // ceil(lo + S) - floor(lo) + 1, which is largest when lo sits just under an
  // integer: floor(S) + 3 for a non-integer S, S + 2 for an integer one. Both are
  // ceil(S) + 2. A measured sweep of 821,859 realistic viewports puts floor(S) + 2
  // — the typical snap — one cell short on 0.21% of them, which is exactly enough
  // to sit in the unsatisfiable state described below.
  const nx = Math.ceil((b.lonMax - b.lonMin) / FIELD_CELL_DEG) + 2;
  const ny = Math.ceil((b.latMax - b.latMin) / FIELD_CELL_DEG) + 2;
  return strideFor(Math.max(1, nx), Math.max(1, ny), budget);
}

/**
 * Fetch the wind + wave grid covering `b` from /api/field. Null when the server has
 * no grid, or the box falls outside the pre-fetched grid.
 *
 * `maxCells` overrides the server's default cell budget (8000). Pass it only to ask
 * for something DELIBERATELY coarser than the map deserves — the locator inset, whose
 * canvas is a couple of hundred pixels tall and physically cannot show a fine grid.
 * A smaller budget is a smaller response, but for a box the map is showing that is a
 * false economy: the same bytes at the default budget land on finer cells.
 *
 * The returned mesh's own latMin/lonMin/... describe THE CELLS THAT CAME BACK — the
 * first sample point to the last — and the renderer must draw against those, never
 * against the request. They are not the same rectangle: the server snaps outward to
 * whole cells, and when it coarsens, the last sampled row/column generally falls short
 * of the far edge by up to a stride.
 */
export async function fetchFieldMesh(b: FieldBounds, maxCells?: number): Promise<FieldFetch> {
  return getMesh(
    `/api/field?latMin=${b.latMin.toFixed(4)}&latMax=${b.latMax.toFixed(4)}` +
      `&lonMin=${b.lonMin.toFixed(4)}&lonMax=${b.lonMax.toFixed(4)}` +
      (maxCells !== undefined && maxCells > 0 ? `&maxCells=${Math.round(maxCells)}` : ""),
  );
}

async function getMesh(url: string): Promise<FieldFetch> {
  let json: unknown;
  try {
    const res = await fetch(url);
    // 204 is the server saying, authoritatively, "no field here". Anything else that
    // is not ok is a failure to ASK, which is a different fact entirely. Both are a
    // HEALTHY source for the Systems board — the server answered.
    if (res.status === 204) {
      reportSystem("field", true, { note: "no coverage at this point" });
      return NO_COVERAGE;
    }
    if (!res.ok) {
      reportSystem("field", false);
      return FETCH_FAILED;
    }
    json = (await res.json()) as unknown;
  } catch {
    reportSystem("field", false); // network dropped, request aborted, body not JSON
    return FETCH_FAILED;
  }

  const o = (typeof json === "object" && json !== null ? json : {}) as Record<string, unknown>;
  const nx = num(o.nx);
  const ny = num(o.ny);
  const times = Array.isArray(o.timesUtc) ? (o.timesUtc.filter((t) => typeof t === "string") as string[]) : [];
  if (nx === null || ny === null || nx < 2 || ny < 2 || times.length === 0) {
    reportSystem("field", false, { note: "malformed response" });
    return FETCH_FAILED;
  }

  // [hour][cell] arrays (number | null) → Float32Array per hour, null → NaN.
  const toHours = (v: unknown): Float32Array[] => {
    if (!Array.isArray(v)) return [];
    return v.map((hour) => {
      const a = new Float32Array(nx * ny).fill(NaN);
      if (Array.isArray(hour)) {
        for (let i = 0; i < hour.length && i < a.length; i++) {
          const x = hour[i];
          if (typeof x === "number") a[i] = x;
        }
      }
      return a;
    });
  };
  const wind = toHours(o.wind);
  const wave = toHours(o.wave);
  if (wind.length === 0 || wave.length === 0) return FETCH_FAILED;
  // Direction planes are optional (older servers omit the keys) — parsed through
  // the SAME converter as the magnitudes, so null/missing cells become NaN and a
  // stitched-in hour with no archived directions goes NaN exactly like its
  // magnitudes do. Never required: their absence must not fail a mesh that is
  // otherwise good, or an old server would blank the whole field.
  const windDir = toHours(o.windDir);
  const waveDir = toHours(o.waveDir);

  const mesh: FieldMesh = {
    latMin: num(o.latMin) ?? 0,
    latMax: num(o.latMax) ?? 0,
    lonMin: num(o.lonMin) ?? 0,
    lonMax: num(o.lonMax) ?? 0,
    nx,
    ny,
    timesUtc: times,
    times: times.map(parseValidTime),
    wind,
    wave,
    cycle: typeof o.cycle === "string" ? o.cycle : "",
    source: typeof o.source === "string" ? o.source : "",
  };
  if (windDir.length > 0) mesh.windDir = windDir;
  if (waveDir.length > 0) mesh.waveDir = waveDir;
  // The cycle is the field's DATA age: "run 12z" that stops advancing means the
  // cron is down even while every one of these fetches succeeds.
  const cm = /^(\d{4})(\d{2})(\d{2})(\d{2})$/.exec(mesh.cycle);
  reportSystem("field", true, {
    dataAt: cm !== null ? Date.UTC(+cm[1], +cm[2] - 1, +cm[3], +cm[4]) : null,
  });
  return { ok: true, mesh };
}
